import { memo, useState } from 'react'

import { useGetContactFlagsByContactIdQuery } from 'clickNgo/entities/contactFlag/api/contactFlagApi'
import { ContactFlags } from 'clickNgo/entities/contactFlag/ui'
import { PartnerGeneralInfoPanel } from 'clickNgo/entities/partner/ui/partnerGeneralInfoPanel'
import { ProfilingCodeType } from 'clickNgo/entities/profilingCode'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import { CopiedText } from 'shared/components/copiedText'
import { SideMenu } from 'shared/components/sideMenu'

import { TContactFormKeys } from '../../model/forms'
import { IContact } from '../../model/types'
import { ContactAdditionalInfo } from '../contactAdditionalInfo'
import { ContactCommonInfo } from '../contactCommonInfo'
import { ContactGeneralInfo } from '../contactGeneralInfo'
import { ContactHPRoles } from '../contactHPRoles'

import { contactPreviewTranslation } from './translation'

import styles from './ContactPreviewContent.module.scss'

type Props = {
  contact: IContact
}

enum ContactPreviewMenuItems {
  Basic = 'basic',
  Additional = 'additional',
  UserRights = 'userRights',
  Campaigns = 'campaigns',
  ToolAccess = 'toolAccess',
}

const READONLY_FORM_KEYS: TContactFormKeys = {
  additional: 'readonly-contact-additional',
  general: 'readonly-contact-general',
  hpRoles: 'readonly-contact-hp-roles',
  common: 'readonly-contact-common',
  settings: 'readonly-contact-settings',
}

export const ContactPreviewContent = memo<Props>(({ contact }) => {
  const [currentMenuItem, setCurrentMenuItem] = useState<ContactPreviewMenuItems>(
    ContactPreviewMenuItems.Basic
  )
  const contactName = `${contact.firstName} ${contact.lastName}`

  const isNewContact = contact.contactId.startsWith('new-')

  const {
    data: contactFlags,
    isLoading,
    isFetching,
  } = useGetContactFlagsByContactIdQuery({
    contactId: contact.contactId,
    topPartnerId: contact.topPartnerId,
  })

  return (
    <div className={styles.preview}>
      <div className={styles.header}>
        <div className={styles.title}>
          <h4>
            <Translation
              text={contactPreviewTranslation.header.title}
              replaceParams={[{ key: '{name}', value: contactName }]}
            />
          </h4>
          <div className={styles.contactAdditional}>
            <div className={styles.label}>
              <Translation text={contactPreviewTranslation.header.contactId} span />
            </div>
            <div className={styles.value}>
              <CopiedText>{contact.contactId}</CopiedText>
            </div>
            <div className={styles.label}>
              <Translation text={contactPreviewTranslation.header.email} span />
            </div>
            <div className={styles.value}>
              <CopiedText>{contact.email}</CopiedText>
            </div>
          </div>
        </div>
        <PartnerGeneralInfoPanel
          communicationName={contact.communicationName}
          locationId={contact.locationId}
          partnerId={contact.partnerId}
        />
      </div>
      {/* Render contact details here */}
      <div className={styles.details}>
        <LoaderOverlay isVisible={isLoading || isFetching} color="white" />
        <SideMenu
          classes={styles}
          keepMounted
          onItemSelectById={(id) => setCurrentMenuItem(id as ContactPreviewMenuItems)}
          selectedItemId={currentMenuItem}
        >
          <SideMenu.Item
            id={ContactPreviewMenuItems.Basic}
            title={contactPreviewTranslation.sideMenu.basic}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <ContactGeneralInfo isReadOnly contact={contact} formKeys={READONLY_FORM_KEYS} />
              <ContactCommonInfo isReadOnly contact={contact} formKeys={READONLY_FORM_KEYS} />
              <ContactHPRoles isReadOnly contact={contact} formKeys={READONLY_FORM_KEYS} />
            </div>
          </SideMenu.Item>
          <SideMenu.Item
            id={ContactPreviewMenuItems.Additional}
            title={contactPreviewTranslation.sideMenu.additional}
          >
            <ContactAdditionalInfo isReadOnly contact={contact} formKeys={READONLY_FORM_KEYS} />
          </SideMenu.Item>
          {!isNewContact && <SideMenu.Separator />}
          <SideMenu.Item
            id={ContactPreviewMenuItems.UserRights}
            title={contactPreviewTranslation.sideMenu.userRights}
            isHidden={isNewContact}
          >
            <ContactFlags
              isReadOnly
              contact={contact}
              contactFlags={contactFlags?.UserRights || []}
              type={ProfilingCodeType.UserRights}
              hasPortalAccess={false}
              market={contact.market}
              region={contact.region}
            />
          </SideMenu.Item>
          <SideMenu.Item
            id={ContactPreviewMenuItems.Campaigns}
            title={contactPreviewTranslation.sideMenu.campaigns}
            isHidden={isNewContact}
          >
            <ContactFlags
              isReadOnly
              contact={contact}
              contactFlags={contactFlags?.Campaigns || []}
              type={ProfilingCodeType.Campaigns}
              hasPortalAccess={false}
              market={contact.market}
              region={contact.region}
            />
          </SideMenu.Item>
          <SideMenu.Item
            id={ContactPreviewMenuItems.ToolAccess}
            title={contactPreviewTranslation.sideMenu.toolAccess}
            isHidden={isNewContact}
          >
            <ContactFlags
              isReadOnly
              contact={contact}
              contactFlags={contactFlags?.Tools || []}
              type={ProfilingCodeType.Tools}
              hasPortalAccess={false}
              market={contact.market}
              region={contact.region}
            />
          </SideMenu.Item>
        </SideMenu>
      </div>
    </div>
  )
})

ContactPreviewContent.displayName = 'ContactPreviewContent'
